import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { permissions } from '@/lib/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageLoading } from '@/components/ui/loading';
import { getStatusBadgeClass, getStatusLabel } from '@/lib/utils';
import { formatWorkedHours } from '@/lib/actual-labour';
import { AuthenticatedPhoto } from '@/components/photographs/AuthenticatedPhoto';
import { ArrowLeft, CheckCircle2, Send, X, Trash2 } from 'lucide-react';
import DecisionModal from '@/components/common/DecisionModal';
import ApprovalHistory from '@/components/common/ApprovalHistory';

export default function ActualStatusDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [showDecision, setShowDecision] = useState<'manager' | 'cluster' | null>(null);
  const [remarks, setRemarks] = useState('');
  const [serverError, setServerError] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState<any | null>(null);

  const { data: record, isLoading } = useQuery({
    queryKey: ['actual-status', id],
    queryFn: async () => {
      const res = await api.get<{ data: any }>(`/actual-status/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/actual-status/${id}/submit`),
    onSuccess: () => {
      addToast('Submitted for Manager approval', 'success');
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['actual-status', id] });
    },
    onError: (err: any) => addToast(err.message || 'Failed', 'error'),
  });

  const closeDecisionModal = () => {
    setShowDecision(null); setRemarks(''); setServerError('');
  };

  const refreshQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['actual-status'] });
    queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
    queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
    queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
    queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
    queryClient.invalidateQueries({ queryKey: ['actual-status', id] });
  };

  const managerDecisionMutation = useMutation({
    mutationFn: (payload: { decision: 'APPROVE' | 'REJECT'; remarks: string }) => api.post(`/actual-status/${id}/manager-decision`, payload),
    onSuccess: (_data, variables) => {
      const msg = variables.decision === 'APPROVE' ? 'Departmental Labour -Actual approved by Manager and sent to Cluster Head.' : 'Departmental Labour -Actual rejected and returned to the Engineer.';
      addToast(msg, variables.decision === 'APPROVE' ? 'success' : 'info');
      refreshQueries();
      closeDecisionModal();
    },
    onError: (err: any) => { setServerError(err.message || 'Failed to save decision'); },
  });

  const clusterDecisionMutation = useMutation({
    mutationFn: (payload: { decision: 'APPROVE' | 'REJECT'; remarks: string }) => api.post(`/actual-status/${id}/cluster-decision`, payload),
    onSuccess: (_data, variables) => {
      const msg = variables.decision === 'APPROVE' ? 'Departmental Labour -Actual finally approved.' : 'Departmental Labour -Actual rejected by Cluster Head.';
      addToast(msg, variables.decision === 'APPROVE' ? 'success' : 'info');
      refreshQueries();
      closeDecisionModal();
    },
    onError: (err: any) => { setServerError(err.message || 'Failed to save decision'); },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/actual-status/${id}`),
    onSuccess: () => {
      addToast('Record deleted', 'success');
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/actuals');
    },
    onError: (err: any) => addToast(err.message || 'Failed to delete', 'error'),
  });

  const isApprovePending =
    (managerDecisionMutation.isPending && managerDecisionMutation.variables?.decision === 'APPROVE') ||
    (clusterDecisionMutation.isPending && clusterDecisionMutation.variables?.decision === 'APPROVE');
  const isRejectPending =
    (managerDecisionMutation.isPending && managerDecisionMutation.variables?.decision === 'REJECT') ||
    (clusterDecisionMutation.isPending && clusterDecisionMutation.variables?.decision === 'REJECT');
  const loadingAction = isApprovePending ? 'APPROVE' : isRejectPending ? 'REJECT' : null;

  const handleDecision = (decision: 'APPROVE' | 'REJECT', remarksValue: string) => {
    setServerError('');
    const payload = { decision, remarks: remarksValue };
    if (showDecision === 'manager') managerDecisionMutation.mutate(payload);
    else clusterDecisionMutation.mutate(payload);
  };

  if (isLoading || !record) return <PageLoading />;

  const roles = user?.roles ?? [];
  const editRec = { status: record.status, createdBy: record.created_by };
  const canSubmit = permissions.canSubmitOperationalRecord(roles, editRec, user?.id);
  const canEdit = permissions.canEditOperationalRecord(roles, editRec, user?.id);
  const canDelete = permissions.canDeleteOperationalRecord(roles, editRec, user?.id);
  const canManagerDecision = record.status === 'PENDING_MANAGER_APPROVAL' && permissions.canManagerDecide(roles) && record.created_by !== user?.id;
  const canClusterDecision = record.status === 'MANAGER_APPROVED' && permissions.canClusterDecide(roles) && record.created_by !== user?.id;

  // Group workers by activity
  const workersByActivity = new Map<string, any[]>();
  for (const w of (record.workers || [])) {
    const aid = w.activity_id || '';
    if (!workersByActivity.has(aid)) workersByActivity.set(aid, []);
    workersByActivity.get(aid)!.push(w);
  }

  const computeActivityTotal = (workers: any[]): number => {
    return workers.reduce((sum: number, w: any) => sum + (w.worker_total || 0), 0);
  };

  // The Worker stores the authoritative header total (which now includes the
  // Additional Lump-sum amounts). Legacy records that predate lumpsum monetary
  // storage fall back to summing worker-row totals plus lumpsum amounts,
  // mirroring approvedActualCostExpr + lumpsum cost.
  const computeGrandTotal = (): number => {
    if (record.total_cost && Number(record.total_cost) > 0) return Number(record.total_cost);
    let total = 0;
    for (const [, workers] of workersByActivity) {
      total += computeActivityTotal(workers);
    }
    for (const l of (record.lumpsum || [])) {
      total += (l.amount || 0);
    }
    return total;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/actuals')}><ArrowLeft size={16} /></Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-heading font-bold text-primary break-words">{record.actual_number}</h1>
            <p className="text-sm text-secondary break-words">{record.project_name} &middot; {record.block_name}</p>
          </div>
        </div>
        <Badge variant={getStatusBadgeClass(record.status)} className="text-sm px-3 py-1">{getStatusLabel(record.status)}</Badge>
      </div>

      <div className="flex flex-wrap gap-3">
        {canEdit && <Link to={`/actuals/${id}/edit`}><Button variant="outline" size="sm">Edit</Button></Link>}
        {canSubmit && <Button size="sm" onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}><Send size={14} className="mr-1" />Submit for Approval</Button>}
        {canDelete && <Button variant="destructive" size="sm" onClick={() => { if (window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) deleteMutation.mutate(); }} loading={deleteMutation.isPending}><Trash2 size={14} className="mr-1" />Delete</Button>}
        {canManagerDecision && <Button size="sm" onClick={() => { setShowDecision('manager'); setRemarks(''); setServerError(''); }}><CheckCircle2 size={14} className="mr-1" />Review</Button>}
        {canClusterDecision && <Button size="sm" onClick={() => { setShowDecision('cluster'); setRemarks(''); setServerError(''); }}><CheckCircle2 size={14} className="mr-1" />Review</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 min-w-0"><p className="text-xs text-secondary">Date</p><p className="font-medium">{record.work_date}</p></CardContent></Card>
        <Card><CardContent className="p-4 min-w-0"><p className="text-xs text-secondary">Created By</p><p className="font-medium break-words">{record.created_by_name}</p></CardContent></Card>
        <Card><CardContent className="p-4 min-w-0"><p className="text-xs text-secondary">Grand Total</p><p className="font-medium">&#8377;{(computeGrandTotal() || 0).toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4 min-w-0"><p className="text-xs text-secondary">Linked Departmental Labour-Requirement</p><p className="font-medium break-words">{record.requirement_number || <span className="text-muted">Legacy &mdash; not linked</span>}</p></CardContent></Card>
      </div>

      {record.activities?.length > 0 && record.activities.map((act: any) => {
        const actWorkers = workersByActivity.get(act.id) || workersByActivity.get('') || [];
        const actTotal = computeActivityTotal(actWorkers);
        return (
          <Card key={act.id}>
            <CardHeader><CardTitle className="text-lg min-w-0"><span className="break-words">{act.activity_name}</span> <span className="text-sm font-normal text-secondary">({act.completed_quantity} {act.unit_name || 'units'})</span></CardTitle></CardHeader>
            <CardContent>
              {actWorkers.length > 0 ? (
                <>
                  <div className="hidden md:block">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b"><th className="text-left py-2">Category</th><th className="text-right py-2">Count</th><th className="text-right py-2">Worked Hrs</th><th className="text-right py-2">Rate (₹/hr)</th><th className="text-right py-2">Total</th><th className="text-right py-2">Time In</th><th className="text-right py-2">Time Out</th></tr></thead>
                      <tbody>{actWorkers.map((w: any) => (
                        <tr key={w.id} className="border-b"><td className="py-2">{w.category_name}</td><td className="py-2 text-right">{w.actual_count}</td><td className="py-2 text-right">{Number(w.duration_hours) > 0 ? formatWorkedHours(Number(w.duration_hours) * 60) : '-'}</td><td className="py-2 text-right">&#8377;{w.rate}</td><td className="py-2 text-right">&#8377;{(w.worker_total || 0).toLocaleString()}</td><td className="py-2 text-right">{w.time_in || '-'}</td><td className="py-2 text-right">{w.time_out || '-'}</td></tr>
                      ))}</tbody>
                      <tfoot><tr className="font-medium border-t"><td colSpan={4} className="py-2 text-right">Activity Total</td><td className="py-2 text-right">&#8377;{actTotal.toLocaleString()}</td><td colSpan={2}></td></tr></tfoot>
                    </table>
                  </div>
                  <div className="md:hidden divide-y divide-border rounded-lg border border-subtle">
                    {actWorkers.map((w: any) => (
                      <div key={w.id} className="p-4 space-y-2">
                        <p className="font-medium text-primary break-words">{w.category_name}</p>
                        <dl className="text-sm space-y-1">
                          <div className="flex justify-between gap-3"><dt className="text-secondary">Count</dt><dd className="text-primary text-right">{w.actual_count}</dd></div>
                          <div className="flex justify-between gap-3"><dt className="text-secondary">Worked Hrs</dt><dd className="text-primary text-right">{Number(w.duration_hours) > 0 ? formatWorkedHours(Number(w.duration_hours) * 60) : '-'}</dd></div>
                          <div className="flex justify-between gap-3"><dt className="text-secondary">Rate</dt><dd className="text-primary text-right">&#8377;{w.rate}</dd></div>
                          <div className="flex justify-between gap-3"><dt className="text-secondary">Total</dt><dd className="text-primary text-right font-medium">&#8377;{(w.worker_total || 0).toLocaleString()}</dd></div>
                          <div className="flex justify-between gap-3"><dt className="text-secondary">Time In</dt><dd className="text-primary text-right">{w.time_in || '-'}</dd></div>
                          <div className="flex justify-between gap-3"><dt className="text-secondary">Time Out</dt><dd className="text-primary text-right">{w.time_out || '-'}</dd></div>
                        </dl>
                      </div>
                    ))}
                    <div className="px-4 py-2 flex justify-between font-medium text-sm border-t">
                      <span>Activity Total</span>
                      <span>&#8377;{actTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-secondary">No workers assigned to this activity.</p>
              )}
            </CardContent>
          </Card>
        );
      })}

      {record.lumpsum?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Additional Lump-sum Requirements</CardTitle></CardHeader>
        <CardContent>
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2">Category</th><th className="text-right py-2">Mandays</th><th className="text-right py-2">Rate (₹/manday)</th><th className="text-right py-2">Amount</th></tr></thead>
              <tbody>{record.lumpsum.map((l: any) => (
                <tr key={l.id} className="border-b"><td className="py-2">{l.category_name}</td><td className="py-2 text-right">{l.mandays}</td><td className="py-2 text-right">&#8377;{(l.rate || 0).toLocaleString('en-IN')}</td><td className="py-2 text-right">&#8377;{(l.amount || 0).toLocaleString('en-IN')}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-border rounded-lg border border-subtle">
            {record.lumpsum.map((l: any) => (
              <div key={l.id} className="p-4 space-y-2">
                <p className="font-medium text-primary break-words">{l.category_name}</p>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between gap-3"><dt className="text-secondary">Mandays</dt><dd className="text-primary text-right">{l.mandays}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-secondary">Rate (₹/manday)</dt><dd className="text-primary text-right">&#8377;{(l.rate || 0).toLocaleString('en-IN')}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-secondary">Amount</dt><dd className="text-primary text-right font-medium">&#8377;{(l.amount || 0).toLocaleString('en-IN')}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Photographs</CardTitle></CardHeader>
        <CardContent>
          {record.attachments?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {record.attachments.map((a: any) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setPreviewPhoto(a)}
                  className="border rounded-lg overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-zoom-in"
                  aria-label={`View photograph ${a.file_name || a.id}`}
                >
                  <AuthenticatedPhoto
                    src={`/actual-status/${record.id}/photographs/${a.id}`}
                    alt={a.file_name || 'Photograph'}
                    className="w-full h-32 object-cover"
                    placeholderClassName="h-32"
                  />
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 py-4 text-secondary">
              <img src="/assests/photo-placeholder.svg" alt="" className="h-10 w-10 object-contain" />
              <p className="text-sm">No photographs attached to this record.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <div className="flex justify-end text-lg font-semibold">
            Grand Total: &#8377;{computeGrandTotal().toLocaleString('en-IN')}
          </div>
        </CardContent>
      </Card>

      <ApprovalHistory history={record.approvalHistory} />

      {previewPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPreviewPhoto(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Photograph preview"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="absolute -top-10 right-0 text-white hover:text-white hover:bg-white/10"
              onClick={() => setPreviewPhoto(null)}
            >
              <X size={16} /> Close
            </Button>
            <div className="border rounded-lg overflow-hidden bg-surface">
              <AuthenticatedPhoto
                src={`/actual-status/${record.id}/photographs/${previewPhoto.id}`}
                alt={previewPhoto.file_name || 'Photograph'}
                className="w-full max-h-[70vh] object-contain bg-black"
                placeholderClassName="h-[50vh]"
              />
              {previewPhoto.file_name && (
                <p className="text-sm px-3 py-2 text-secondary">{previewPhoto.file_name}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <DecisionModal
        open={!!showDecision}
        title={showDecision === 'manager' ? 'Manager Decision' : 'Cluster Head Decision'}
        recordLabel="Record:"
        recordNumber={record.actual_number}
        engineerName={record.created_by_name}
        projectName={record.project_name}
        remarks={remarks}
        onRemarksChange={(value) => { setRemarks(value); setServerError(''); }}
        serverError={serverError}
        loadingAction={loadingAction}
        onApprove={(remarksValue) => handleDecision('APPROVE', remarksValue)}
        onReject={(remarksValue) => handleDecision('REJECT', remarksValue)}
        onCancel={closeDecisionModal}
      />
    </div>
  );
}


